import { StrictMode, useEffect } from 'react';
import type { FC } from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import amplifyConfig from './config/amplify';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { AudioStateProvider } from './contexts/AudioStateContext';
import { BalanceProvider } from './contexts/BalanceContext';
import { I18nProvider } from './i18n/I18nContext';
import { router } from './App';

import { useSettings } from './contexts/SettingsContext';
import { useTranslation } from './i18n/I18nContext';
import { unregisterServiceWorker } from './utils/unregister-sw';

import './index.css';

Amplify.configure(amplifyConfig);

// Clean up the workbox service worker that vite-plugin-pwa used to ship.
// No-op for new visitors; unregisters + clears caches for returning ones.
unregisterServiceWorker();

// Pre-server-side vocab lived in localStorage under this key. Drop it on
// first load post-migration so it doesn't sit as dead data forever.
try { localStorage.removeItem('polyreader_vocab'); } catch {}

/** Sync persisted settings into global side-effects (i18n + theme class).
 *  Fires on initial load and whenever the user changes the relevant setting. */
const SettingsSync: FC<{ children: React.ReactNode }> = ({ children }) => {
  const { settings, loading } = useSettings();
  const { setLanguage } = useTranslation();

  useEffect(() => {
    if (loading) return;
    setLanguage(settings.language);
  }, [loading, settings.language, setLanguage]);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', settings.theme === 'dark');
  }, [settings.theme]);

  return <>{children}</>;
};

ReactDOM.createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AuthProvider>
      <ToastProvider>
        <AudioStateProvider>
          <SettingsProvider>
            <BalanceProvider>
              <I18nProvider>
                <SettingsSync>
                  <RouterProvider router={router} />
                </SettingsSync>
              </I18nProvider>
            </BalanceProvider>
          </SettingsProvider>
        </AudioStateProvider>
      </ToastProvider>
    </AuthProvider>
  </StrictMode>,
);
